import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Layers, RotateCw, ChevronLeft, ChevronRight, Shuffle, BookOpen, Check, X, Star, Lock, Crown, Sparkles } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const Flashcards = ({ auth }) => {
  const { t } = useLanguage();
  const [flashcards, setFlashcards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState("all");
  const [chapters, setChapters] = useState([]);
  const [knownCards, setKnownCards] = useState(new Set());
  const [reviewCards, setReviewCards] = useState(new Set());
  const [studyMode, setStudyMode] = useState("all");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isFreeUser = !auth.user || auth.user.subscription_tier === "free";
  const FREE_CARD_LIMIT = 10;
  const FREE_SECTION = "dates"; // Free users only get Dates flashcards

  // Load saved flashcard progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const response = await axios.get(`${API}/flashcards/progress`, {
          withCredentials: true,
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {}
        });
        if (response.data.known_cards?.length) {
          setKnownCards(new Set(response.data.known_cards));
        }
        if (response.data.review_cards?.length) {
          setReviewCards(new Set(response.data.review_cards));
        }
      } catch (error) {
        // Silent fail — just means no saved progress
      }
    };
    if (auth.token) loadProgress();
  }, [auth.token]);

  // Save progress whenever known/review cards change (debounced)
  useEffect(() => {
    if (!auth.token) return;
    if (knownCards.size === 0 && reviewCards.size === 0) return;
    
    const saveTimeout = setTimeout(async () => {
      try {
        await axios.post(`${API}/flashcards/progress`, {
          known_cards: [...knownCards],
          review_cards: [...reviewCards],
          category: selectedChapter
        }, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${auth.token}` }
        });
      } catch (error) {
        // Silent fail — non-critical
      }
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(saveTimeout);
  }, [knownCards, reviewCards, auth.token, selectedChapter]);

  useEffect(() => {
    fetchChapters();
    // Free users start with dates section
    if (isFreeUser) {
      fetchFlashcards("dates");
      setSelectedChapter("dates");
    } else {
      fetchFlashcards("all");
    }
  }, []);

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API}/chapters`);
      setChapters(response.data);
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  };

  const fetchFlashcards = async (chapter) => {
    setLoading(true);
    try {
      let response;
      if (chapter === "all") {
        response = await axios.get(`${API}/flashcards?limit=200`);
      } else {
        response = await axios.get(`${API}/flashcards/${chapter}`);
      }
      // API returns array directly, not wrapped in {flashcards: [...]}
      const cards = Array.isArray(response.data) ? response.data : (response.data.flashcards || []);
      setFlashcards(cards);
      setCurrentIndex(0);
      setIsFlipped(false);
      // Don't reset known/review — they persist across chapters
    } catch (error) {
      console.error("Error fetching flashcards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChapterChange = (value) => {
    setSelectedChapter(value);
    fetchFlashcards(value);
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  }, [currentIndex]);

  const handleShuffle = () => {
    const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
    setFlashcards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const markAsKnown = () => {
    const cardId = currentCard?.id;
    if (cardId) {
      setKnownCards(prev => new Set([...prev, cardId]));
      setReviewCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    }
    handleNext();
  };

  const markForReview = () => {
    const cardId = currentCard?.id;
    if (cardId) {
      setReviewCards(prev => new Set([...prev, cardId]));
      setKnownCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardId);
        return newSet;
      });
    }
    handleNext();
  };

  // Get filtered cards - computed first so it's available for other functions
  const getFilteredCards = useCallback(() => {
    // Safety check: ensure flashcards is an array
    if (!Array.isArray(flashcards) || flashcards.length === 0) {
      return [];
    }
    
    let cards = flashcards;
    
    // Limit cards for free users
    if (isFreeUser) {
      cards = flashcards.slice(0, FREE_CARD_LIMIT);
    }
    
    if (studyMode === "unknown") {
      return cards.filter(card => !knownCards.has(card.id));
    } else if (studyMode === "review") {
      return cards.filter(card => reviewCards.has(card.id));
    }
    return cards;
  }, [flashcards, isFreeUser, studyMode, knownCards, reviewCards]);

  const filteredCards = getFilteredCards();
  const currentCard = filteredCards[currentIndex];
  const displayedCardCount = isFreeUser ? Math.min(flashcards.length, FREE_CARD_LIMIT) : flashcards.length;
  const progress = displayedCardCount > 0 ? ((knownCards.size / displayedCardCount) * 100) : 0;
  const totalCardsInDB = flashcards.length;

  // Check if free user reached limit
  const reachedFreeLimit = isFreeUser && currentIndex >= FREE_CARD_LIMIT - 1;

  const handleNext = useCallback(() => {
    if (isFreeUser && currentIndex >= FREE_CARD_LIMIT - 1) {
      setShowUpgradeModal(true);
      return;
    }
    // Safety check for filteredCards length
    const cardsLength = filteredCards?.length || 0;
    if (currentIndex < cardsLength - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  }, [currentIndex, filteredCards?.length, isFreeUser]);

  // Keyboard navigation - moved after handleNext is defined
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        if (isFlipped) {
          handleNext();
        } else {
          handleFlip();
        }
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "Enter") {
        handleFlip();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isFlipped, handleNext, handlePrev]);

  const getCategoryColor = (category) => {
    const colors = {
      "Key Fact": "bg-truenorth text-white",
      "Important Date": "bg-maple text-white",
      "Important Person": "bg-purple-600 text-white",
      "General": "bg-slate-600 text-white",
    };
    return colors[category] || "bg-slate-500 text-white";
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="flashcards-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">{t('flashcards.title')}</h1>
          <p className="text-slate-600">{t('flashcards.subtitle')}</p>
        </div>

        {/* Chapter Selection - Prominent */}
        <Card className="border-2 border-maple/20 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3">
              <label className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-maple" />
                Select Category to Study
              </label>
              <div className="flex flex-wrap gap-2">
                {/* Dates - First and FREE */}
                <Button
                  variant={selectedChapter === "dates" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleChapterChange("dates")}
                  className={selectedChapter === "dates" ? "bg-yellow-500 hover:bg-yellow-600 text-white" : "border-yellow-400 text-yellow-700 hover:bg-yellow-50"}
                >
                  <span className="mr-1">📅</span> Important Dates
                  {isFreeUser && <Badge className="ml-1 bg-green-500 text-white text-[10px] px-1">FREE</Badge>}
                </Button>
                
                {/* People - Second */}
                <Button
                  variant={selectedChapter === "personalities" ? "default" : "outline"}
                  size="sm"
                  onClick={() => isFreeUser ? setShowUpgradeModal(true) : handleChapterChange("personalities")}
                  className={`${selectedChapter === "personalities" ? "bg-purple-500 hover:bg-purple-600 text-white" : "border-purple-300 text-purple-700 hover:bg-purple-50"} ${isFreeUser ? "opacity-70" : ""}`}
                >
                  <span className="mr-1">👤</span> Key People
                  {isFreeUser && <Lock className="w-3 h-3 ml-1" />}
                </Button>

                {/* Divider */}
                <div className="w-full border-t border-slate-200 my-1"></div>
                
                {/* All Chapters */}
                <Button
                  variant={selectedChapter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => isFreeUser ? setShowUpgradeModal(true) : handleChapterChange("all")}
                  className={`${selectedChapter === "all" ? "bg-maple hover:bg-maple-600" : ""} ${isFreeUser ? "opacity-70" : ""}`}
                >
                  All Chapters
                  {isFreeUser && <Lock className="w-3 h-3 ml-1" />}
                </Button>
                
                {/* Individual Chapters */}
                {chapters.map((chapter) => {
                  const chapterId = chapter.chapter_id || chapter.id;
                  const chapterNum = chapterId?.replace('chapter_', '');
                  const isLocked = isFreeUser;
                  return (
                    <Button
                      key={chapterId}
                      variant={selectedChapter === chapterId ? "default" : "outline"}
                      size="sm"
                      onClick={() => isLocked ? setShowUpgradeModal(true) : handleChapterChange(chapterId)}
                      className={`${selectedChapter === chapterId ? "bg-maple hover:bg-maple-600" : ""} ${isLocked ? "opacity-70" : ""}`}
                    >
                      Ch {chapterNum}
                      {isLocked && <Lock className="w-3 h-3 ml-1" />}
                    </Button>
                  );
                })}
              </div>
              {selectedChapter !== "all" && (
                <p className="text-sm text-slate-500">
                  Showing: {selectedChapter === "dates" ? "Important Dates (66 cards)" : 
                           selectedChapter === "personalities" ? "Important Personalities (21 cards)" : 
                           chapters.find(c => (c.chapter_id || c.id) === selectedChapter)?.title || selectedChapter}
                  {isFreeUser && selectedChapter === "dates" && <span className="text-green-600 ml-2">• 10 cards unlocked for free</span>}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Bar */}
        <Card className="border-0 shadow-sm bg-gradient-to-r from-maple-50 to-truenorth-50">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-maple">
                    {isFreeUser ? Math.min(flashcards.length, FREE_CARD_LIMIT) : flashcards.length}
                  </p>
                  <p className="text-xs text-slate-600">{isFreeUser ? "Unlocked Cards" : "Total Cards"}</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{knownCards.size}</p>
                  <p className="text-xs text-slate-600">Known</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{reviewCards.size}</p>
                  <p className="text-xs text-slate-600">Review</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Mastery:</span>
                <Progress value={progress} className="w-32 h-2" />
                <span className="text-sm font-medium text-maple">{Math.round(progress)}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Study Mode Buttons */}
        <div className="flex gap-2 justify-center">
          <Button
            variant={studyMode === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStudyMode("all"); setCurrentIndex(0); setIsFlipped(false); }}
          >
            <Layers className="w-4 h-4 mr-1" /> All ({isFreeUser ? Math.min(flashcards.length, FREE_CARD_LIMIT) : flashcards.length})
          </Button>
          <Button
            variant={studyMode === "unknown" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStudyMode("unknown"); setCurrentIndex(0); setIsFlipped(false); }}
          >
            <BookOpen className="w-4 h-4 mr-1" /> Learning ({(isFreeUser ? Math.min(flashcards.length, FREE_CARD_LIMIT) : flashcards.length) - knownCards.size})
          </Button>
          <Button
            variant={studyMode === "review" ? "default" : "outline"}
            size="sm"
            onClick={() => { setStudyMode("review"); setCurrentIndex(0); setIsFlipped(false); }}
          >
            <Star className="w-4 h-4 mr-1" /> Review ({reviewCards.size})
          </Button>
        </div>

        {/* Flashcard */}
        {currentCard ? (
          <div className="perspective-1000">
            <div
              className={`relative w-full h-80 cursor-pointer transition-transform duration-500 transform-style-preserve-3d ${
                isFlipped ? "rotate-y-180" : ""
              }`}
              onClick={handleFlip}
              data-testid="flashcard"
            >
              {/* Front of card */}
              <Card className={`absolute w-full h-full backface-hidden ${isFlipped ? "invisible" : ""}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(currentCard.category)}`}>
                      {currentCard.category}
                    </span>
                    <span className="text-sm text-slate-500">
                      {currentIndex + 1} / {filteredCards.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-center h-56">
                  <p className="text-xl text-center font-medium text-slate-800 px-6">
                    {currentCard.front}
                  </p>
                </CardContent>
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <p className="text-sm text-slate-400">Click or press Space to flip</p>
                </div>
              </Card>

              {/* Back of card */}
              <Card className={`absolute w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-truenorth-50 to-blue-50 ${!isFlipped ? "invisible" : ""}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-truenorth">Answer</span>
                    <span className="text-sm text-slate-500">
                      {currentCard.chapter_title || ""}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-center h-56 overflow-y-auto">
                  <p className="text-lg text-center text-slate-800 px-6 whitespace-pre-wrap">
                    {currentCard.back}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="h-80 flex items-center justify-center">
            <div className="text-center">
              <Layers className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No flashcards available in this mode</p>
              <Button variant="outline" className="mt-4" onClick={() => setStudyMode("all")}>
                View All Cards
              </Button>
            </div>
          </Card>
        )}

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <Button
            variant="outline"
            onClick={markForReview}
            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            disabled={!currentCard}
          >
            <Star className="w-4 h-4 mr-2" /> Review Later
          </Button>
          
          <Button
            variant="outline"
            onClick={handleFlip}
            className="px-6"
          >
            <RotateCw className="w-4 h-4 mr-2" /> Flip
          </Button>
          
          <Button
            variant="outline"
            onClick={markAsKnown}
            className="text-green-600 border-green-300 hover:bg-green-50"
            disabled={!currentCard}
          >
            <Check className="w-4 h-4 mr-2" /> Know It
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            disabled={currentIndex === filteredCards.length - 1}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Shuffle Button */}
        <div className="flex justify-center">
          <Button variant="ghost" onClick={handleShuffle} className="text-slate-600">
            <Shuffle className="w-4 h-4 mr-2" /> Shuffle Cards
          </Button>
        </div>

        {/* Keyboard shortcuts hint */}
        <div className="text-center text-sm text-slate-400">
          <p>Keyboard: ← Previous | Space/Enter Flip | → Next</p>
        </div>

        {/* Free user limit banner */}
        {isFreeUser && (
          <Card className="border-2 border-yellow-300 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium text-slate-900">
                      Free: 10 Important Dates flashcards unlocked
                    </p>
                    <p className="text-sm text-slate-600">Upgrade to access all 200 flashcards across all chapters</p>
                  </div>
                </div>
                <Link to="/pricing">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-white">
                    <Crown className="w-4 h-4 mr-2" />
                    Unlock All
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUpgradeModal(false)}>
          <Card className="max-w-md w-full border-2 border-yellow-400 shadow-2xl" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                Unlock All Flashcard Categories
              </h3>
              <p className="text-slate-600 mb-4">
                Free users have access to 10 Important Dates flashcards. Upgrade to unlock all 200 flashcards including chapter-specific questions and key personalities!
              </p>

              <div className="bg-slate-50 rounded-lg p-4 mb-4 text-left">
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    66 Important Dates flashcards
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    21 Key Personalities flashcards
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    113 Chapter-specific quiz questions
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    Progress tracking & mastery
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <Link to="/pricing" className="block">
                  <Button className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-white font-semibold h-12">
                    <Crown className="w-5 h-5 mr-2" />
                    Upgrade — From CAD $9.99
                  </Button>
                </Link>
                <p className="text-xs text-slate-500">
                  <span className="line-through">$29.99</span> → <span className="font-semibold text-red-600">$14.99 Lifetime</span> (50% OFF)
                </p>
                <Button variant="ghost" onClick={() => setShowUpgradeModal(false)} className="w-full">
                  Maybe Later
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </DashboardLayout>
  );
};

export default Flashcards;

